// AAB download-link validity.
//
// The download button used to gate solely on a server-stamped completion time
// (Redis `aab:completed_at:`), treating a missing stamp as "expired". But that
// stamp is only written for dashboard-tracked in-progress builds — a new-app
// auto-AAB (IS_NEW_APP=true) or a CI-pushed URL never gets one, so a link that
// was just created showed as expired. The presigned URL already carries its
// own expiry, which is the ground truth for whether it still works — prefer it.

/**
 * Absolute expiry (epoch ms) of a presigned AAB URL, parsed from its own query
 * params, or null if the URL has no recognisable expiry.
 */
export function aabUrlExpiry(url?: string | null): number | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    // GCS V2 signed URL — `Expires` is absolute Unix seconds.
    const gcs = u.searchParams.get('Expires');
    if (gcs && /^\d+$/.test(gcs)) return Number(gcs) * 1000;
    // AWS / R2 SigV4 — `X-Amz-Date` (basic ISO) + `X-Amz-Expires` (relative s).
    const amzDate = u.searchParams.get('X-Amz-Date');
    const amzExp = u.searchParams.get('X-Amz-Expires');
    if (amzDate && amzExp && /^\d+$/.test(amzExp)) {
      const m = amzDate.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/);
      if (m) {
        const start = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]);
        return start + Number(amzExp) * 1000;
      }
    }
  } catch { /* not a parseable URL */ }
  return null;
}

/** Default download window (matches the CI presign TTL) for links without a parseable expiry. */
export const AAB_WINDOW_MS = 2 * 60 * 60 * 1000;

/**
 * Whether the AAB download link is currently usable. Prefers the URL's own
 * expiry; falls back to the completion-timestamp window when the URL carries none.
 */
export function isAabDownloadable(aabUrl?: string | null, aabCompletedAt?: number | null): boolean {
  if (!aabUrl) return false;
  const expiry = aabUrlExpiry(aabUrl);
  if (expiry != null) return Date.now() < expiry;
  return !!aabCompletedAt && aabCompletedAt > 0 && (Date.now() - aabCompletedAt) < AAB_WINDOW_MS;
}

/** Ms remaining on the AAB link, or 0 if unknown/expired. */
export function aabMsRemaining(aabUrl?: string | null, aabCompletedAt?: number | null): number {
  const expiry = aabUrlExpiry(aabUrl);
  if (expiry != null) return Math.max(0, expiry - Date.now());
  if (aabCompletedAt && aabCompletedAt > 0) return Math.max(0, AAB_WINDOW_MS - (Date.now() - aabCompletedAt));
  return 0;
}
